/**
 * Launch tracking for journey map steps whose completion arrives out of band.
 *
 * Mindtickle (and anything else fed by a nightly Snowflake sync) cannot tell us the moment a
 * learner finishes a course: the completion lands in the warehouse hours later. Without a
 * record of the launch, the step would sit at "not started" all day even though the learner
 * did the work, so `POST db/journey-map-item/{id}/launch/` records the attempt and puts the
 * step into "awaiting confirmation" until the sync either confirms or the grace period lapses.
 *
 * Relaunching is deliberately re-reported: each launch refreshes `last_launched_at`, which is
 * what the staleness clock is measured from, so a learner who retries does not get flagged as
 * unconfirmed for an attempt they have since repeated.
 *
 * IMPORTANT — why this is not keyed on `completion_mode`:
 * `external_sync` is the *default* completion mode for new items, so most markdown, file and
 * plain-link steps carry it without meaning it. Calling the launch endpoint for all of them
 * would push ordinary links into "awaiting confirmation" and never resolve, because nothing
 * upstream will ever report on them. `shouldTrackExternalLaunch` therefore requires a
 * provider that genuinely reports asynchronously, or an explicit opt-in on the item.
 */
import { buildApiUrl, getApiHeaders } from '../queriesHelpers';

export type JourneyMapSyncStatus =
  | 'not_required'
  | 'pending'
  | 'in_sync'
  | 'validated'
  | 'failed'
  | 'stale'
  | 'no_match';

export type JourneyMapSyncState = {
  is_pending: boolean;
  is_stale: boolean;
  is_launch_tracked: boolean;
  sync_status: JourneyMapSyncStatus | string | null;
  pending_since?: string | null;
  stale_after?: string | null;
  grace_days?: number | null;
  launch_count?: number | null;
  last_launched_at?: string | null;
};

export type JourneyMapLaunchResult = {
  /** The `JourneyMapItemProgress` row as the API serializes it. */
  progress: any;
  pending: boolean;
  already_completed: boolean;
  launch_url: string | null;
  sync_state: JourneyMapSyncState | null;
};

/** Providers whose completion is reported asynchronously rather than by the app itself. */
const ASYNC_SYNC_PROVIDER_KEYS = ['mindtickle'];

/** Content reference types that imply an asynchronously-reported provider. */
const ASYNC_SYNC_CONTENT_REF_TYPES = ['mindtickle_series'];

const normalize = (value: any): string => String(value || '').trim().toLowerCase();

export function isAsyncSyncProvider(item: any): boolean {
  const provider = normalize(item?.provider);
  const contentRefType = normalize(item?.content_ref_type);

  return (
    ASYNC_SYNC_PROVIDER_KEYS.some((key) => provider.includes(key)) ||
    ASYNC_SYNC_CONTENT_REF_TYPES.includes(contentRefType)
  );
}

/**
 * Whether launching this item should be reported to the API as a pending attempt.
 *
 * `provider_config.pending_validation.enabled` is the generic opt-in an author (or a future
 * provider) can set to get the same treatment without being Mindtickle.
 */
export function shouldTrackExternalLaunch(item: any): boolean {
  if (!item?.id) return false;

  if (isAsyncSyncProvider(item)) return true;

  const pendingConfig =
    item?.provider_config?.pending_validation ?? item?.provider_config?.pendingValidation ?? null;

  return pendingConfig?.enabled === true;
}

function coerceSyncState(payload: any): JourneyMapSyncState | null {
  if (!payload || typeof payload !== 'object') return null;

  return {
    is_pending: payload.is_pending === true,
    is_stale: payload.is_stale === true,
    is_launch_tracked: payload.is_launch_tracked === true,
    sync_status: payload.sync_status ?? null,
    pending_since: payload.pending_since ?? null,
    stale_after: payload.stale_after ?? null,
    grace_days: payload.grace_days ?? null,
    launch_count: payload.launch_count ?? null,
    last_launched_at: payload.last_launched_at ?? null,
  };
}

/**
 * Record that the learner opened this item's external content.
 *
 * The API resolves the caller's active assignment itself when `assignmentId` is omitted, so
 * this is safe to call from a viewer that has not loaded the assignment yet.
 */
export async function recordJourneyMapItemLaunch(
  journeyMapItemId: number | string,
  assignmentId?: number | string | null,
): Promise<JourneyMapLaunchResult> {
  const endpoint = `db/journey-map-item/${journeyMapItemId}/launch/`;

  const body: Record<string, any> = {};

  if (assignmentId != null && assignmentId !== '') {
    body.assignment = assignmentId;
  }

  const response = await fetch(buildApiUrl(endpoint, {}, true), {
    method: 'POST',
    headers: getApiHeaders(null, 'POST', endpoint),
    credentials: 'include',
    body: JSON.stringify(body),
  });

  const text = await response.text();

  let parsed: any = null;

  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }

  if (!response.ok) {
    const error: any = new Error(
      String(parsed?.detail || parsed || `Unable to record the launch (${response.status}).`),
    );
    error.status = response.status;
    error.payload = parsed;
    throw error;
  }

  const launch = parsed?.launch || {};

  return {
    progress: parsed || null,
    pending: launch.pending === true,
    already_completed: launch.already_completed === true,
    launch_url: launch.launch_url ?? null,
    sync_state: coerceSyncState(launch.sync_state),
  };
}

/** Read the sync state off a progress row, whichever shape the caller has to hand. */
export function readSyncState(row: any): JourneyMapSyncState | null {
  return coerceSyncState(row?.sync_state ?? row?.progress?.sync_state ?? null);
}

export const AWAITING_CONFIRMATION_MESSAGE =
  'Completion is awaiting nightly confirmation from the external system. This usually clears within a day.';

export const UNCONFIRMED_MESSAGE =
  'We recorded that you started this, but no completion has come back from the external system. Relaunch it to finish, or ask for a manual review.';

/**
 * Learner-facing explanation for a launch-tracked step, phrased around what the learner can
 * do rather than around the sync machinery. `is_stale` means the grace period has lapsed
 * without a confirmation, so the reassuring "wait for tonight" wording is no longer honest.
 */
export function describeLaunchSyncState(state?: JourneyMapSyncState | null): string {
  if (!state?.is_pending) return '';

  return state.is_stale ? UNCONFIRMED_MESSAGE : AWAITING_CONFIRMATION_MESSAGE;
}

/** Short badge text next to the step title. */
export function launchSyncTagLabel(state?: JourneyMapSyncState | null): string {
  if (!state?.is_pending) return '';

  return state.is_stale ? 'Not Confirmed' : 'Awaiting Confirmation';
}
